import type { Intersection, Object3D, Vector3 } from 'three';
import type { RootState } from '@react-three/fiber/dist/declarations/src/core/store';
import RaycasterClass from '@modules/three/common/raycast';
import type { ITool } from '@modules/three/tools/ITool';
import type { ThreeRootState } from '@modules/three/common/BaseThreeScene';

/**
 * Everything the tool needs from the scene. Kept as a callback interface so the tool stays
 * decoupled from ExportScene and reads live values (radius, points) at interaction time.
 */
export interface TrimThickeningHost {
  /** Meshes to raycast (the active jaw's gum + teeth). */
  getRaycastTargets(): Object3D[];
  /** Trim-curve points in world space (index = selection index). */
  getTrimPoints(): Vector3[];
  /** Brush sphere radius in world units (mm). */
  getBrushRadius(): number;
  /** Already-committed selection — a new stroke extends it (union). */
  getSelected(): number[];
  /** Position/orient the brush ring at the surface hit (null hides it). */
  setBrush(point: Vector3 | null, normal: Vector3 | null): void;
  /** Transient highlight of points under the brush (before/without a stroke). */
  onHover(indices: number[]): void;
  /** Transient highlight of the in-progress stroke selection. */
  onPreview(indices: number[]): void;
  /** Finalize the stroke selection (persist to the store). */
  onCommit(indices: number[]): void;
  /** Toggle orbit controls (off while brushing so drag paints instead of rotating). */
  setControlsEnabled(enabled: boolean): void;
}

/**
 * Brush tool for the trim-curve thickening phase. On hover it highlights trim points inside a
 * sphere around the surface hit; press-drag accumulates them (union with the existing
 * selection); release commits. It raycasts the jaw meshes itself (the scene's auto-raycast is
 * non-recursive and wouldn't reach teeth/gum nested under the jaw).
 */
export class TrimThickeningTool implements ITool {
  readonly name = 'trim-thickening';
  private dragging = false;
  private readonly stroke = new Set<number>();

  constructor(private readonly host: TrimThickeningHost) {}

  onDeactivate(): void {
    this.dragging = false;
    this.stroke.clear();
    this.host.setBrush(null, null);
    this.host.onHover([]);
    this.host.onPreview([]);
  }

  onPointerDown(ev: PointerEvent, _hit: Intersection | null, three: RootState | ThreeRootState): void {
    const hit = this.raycast(ev, three);
    if (!hit) {
      this.host.setBrush(null, null);
      return;
    }
    this.dragging = true;
    this.host.setControlsEnabled(false);
    this.stroke.clear();
    for (const i of this.host.getSelected()) this.stroke.add(i); // extend existing selection
    this.applyBrush(hit, true);
  }

  onPointerMove(ev: PointerEvent, _hit: Intersection | null, three: RootState | ThreeRootState): void {
    const hit = this.raycast(ev, three);
    if (!hit) {
      this.host.setBrush(null, null);
      if (!this.dragging) this.host.onHover([]);
      return;
    }
    this.applyBrush(hit, this.dragging);
  }

  onPointerUp(_ev: PointerEvent, _hit: Intersection | null, _three: RootState | ThreeRootState): void {
    if (!this.dragging) return;
    this.dragging = false;
    this.host.setControlsEnabled(true);
    this.host.onCommit([...this.stroke]);
    this.host.onPreview([]);
  }

  /** Raycast the jaw targets directly (recursive) and return the first hit. */
  private raycast(ev: PointerEvent, three: RootState | ThreeRootState): Intersection | null {
    const targets = this.host.getRaycastTargets();
    if (targets.length === 0) return null;
    const result = RaycasterClass.getFirstObjectsIntersection(ev, targets, three.camera, true);
    return result?.intersection ?? null;
  }

  /** Highlight (or accumulate) the trim points inside the brush sphere around the hit. */
  private applyBrush(hit: Intersection, accumulate: boolean): void {
    const normal = hit.face?.normal ?? hit.normal ?? null;
    this.host.setBrush(hit.point, normal);

    const inRadius = this.pointsInRadius(hit.point);
    if (accumulate) {
      for (const i of inRadius) this.stroke.add(i);
      this.host.onPreview([...this.stroke]);
    } else {
      this.host.onHover(inRadius);
    }
  }

  private pointsInRadius(center: Vector3): number[] {
    const radius = this.host.getBrushRadius();
    const r2 = radius * radius;
    const points = this.host.getTrimPoints();
    const indices: number[] = [];
    for (let i = 0; i < points.length; i++) {
      if (points[i].distanceToSquared(center) <= r2) indices.push(i);
    }
    return indices;
  }
}
